<script>
import AdministrationManagementTableAction from '@/components/profile/administrationManagement/AdministrationManagementTableAction.vue'
import { mapGetters } from 'vuex'

export default {
  components: {
    TableAction: AdministrationManagementTableAction
  },
  data: vm => ({
    search: '',
    loading: false,
    toggleLoading: false,
    hasInput: vm.$permissions.hasInput,
    inboxNameModel: '',
    deletionDeadlineModel: null,
    inboxes: [],
    members: []
  }),
  computed: {
    ...mapGetters(['deletionDeadline']),
    computedHeaders() {
      return [
        {
          text: '#',
          align: 'start',
          value: 'id'
        },
        {
          text: this.$t('global.name.table'),
          align: 'start',
          value: 'inboxname'
        },
        {
          text: this.$t('component.inbox.deletion.deadline.in.days'),
          align: 'start',
          value: 'deletionDeadline',
          sortable: false
        },
        {
          text: this.$t('global.action'),
          value: 'action',
          align: 'end',
          sortable: false
        }
      ]
    },
    accepted() {
      return status => status === this.$enums.InvitationStatus.Accepted
    },
    filteredInboxes() {
      return this.inboxes.filter(inbox => inbox.inboxname !== 'Posteingang')
    },
    errorNotification() {
      return this.$t('component.inbox.deletion.deadline.range')
      // @TODO: It seems the $cms implementation is not working as expected
      // return 'Der Wert muss zwischen ' + this.deletionDeadline.min + ' und ' + this.deletionDeadline.max + ' liegen'
    }
  },
  created() {
    this.$store.subscribeAction((action) => {
      if (action.type === 'refreshInboxesList') {
        this.getInboxesList()
      }
    })
    if (this.hasInput) {
      this.getInboxesList()
    }
  },
  methods: {
    getInboxesList() {
      this.loading = true
      this.$inboxService
        .getInboxesList(this.$store?.getters?.administrationTabSelectedOrganization?.id)
        .then(inboxes => {
          this.inboxes = inboxes.data
        })
        .catch(error => {
          console.error('error getting inboxes list', error)
          this.$notify(this.$t('component.inbox.list.error'), { type: 'error' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    deleteInbox(inbox) {
      this.$inboxService
        .deleteInbox(inbox)
        .then(() => {
          this.$notify(this.$t('component.inbox.delete.success'), {
            type: 'success'
          })
          this.getInboxesList()
        })
        .catch(() => {
          this.$notify(this.$t('component.inbox.delete.error'), {
            type: 'error'
          })
        })
    },
    updateInbox(inbox) {
      this.$inboxService
        .updateInbox(inbox, this.inboxNameModel, this.deletionDeadlineModel)
        .then(() => {
          this.$notify(this.$t('component.inbox.update.success'), {
            type: 'success'
          })
          this.getInboxesList()
        })
        .catch(e => {
          if (e.response.status === 422) {
            this.$notify(this.$t('component.inbox.already.existing.error'), {
              type: 'error'
            })
          }
          return this.$notify(this.$t('component.inbox.update.error'), {
            type: 'error'
          })
        })
        .finally(() => {
          this.inboxNameModel = ''
          this.deletionDeadlineModel = null
        })
    },
    customFilter(value, search) {
      if (!search) return true
      const normalizedSearch = search
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '')
      const normalizedValue = (value || '')
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '')
      return normalizedValue.includes(normalizedSearch)
    }
  }
}
</script>
<template>
  <div>
    <v-card>
      <v-card-title>
        <v-text-field
          id="search-inbox-text"
          v-model="search"
          append-icon="mdi-magnify"
          :label="$t('global.search.label')"
          single-line
          outlined
          dense
          hide-details
        >
        </v-text-field>
      </v-card-title>
      <!--      TABLE filtered (!Posteingang since it's default)-->
      <v-data-table
        :headers="computedHeaders"
        :items="filteredInboxes"
        :search="search"
        :custom-filter="customFilter"
        :loading="loading"
        :header-props="{ sortByText: $t('global.sort.by.text') }"
        :sort-by="['inboxname']"
        :footer-props="{
          'items-per-page-text': $t('global.items.per.page.text'),
          'items-per-page-all-text': $t('global.items.per.page.all.text'),
          'page-text': $t('global.page.text'),
          'items-per-page-options': [25, 50, 100]
        }"
        item-key="Id"
        class="elevation-1"
        fixed-header
        calculate-widths>
        <template #no-data>
          {{ $t('global.no.entries') }}
        </template>
        <template #[`item.deletionDeadline`]="{ item }">
          <span>{{ item.deletionDeadline }}</span>
        </template>
        <template #[`item.action`]="{ item }">
          <div class="text-end">
            <TableAction
              confirmation
              icon="mdi-pencil"
              color="primary"
              :disabled="
                !(
                  inboxNameModel &&
                  deletionDeadlineModel >= deletionDeadline.min &&
                  deletionDeadlineModel <= deletionDeadline.max
                )
              "
              :title="$t('component.inbox.update')"
              @activated="
                () => {
                  inboxNameModel = item.inboxname
                  deletionDeadlineModel = item.deletionDeadline
                }
              "
              @confirmed="updateInbox(item)"
            >
              <template slot="confirmationContent">
                <v-text-field
                  id="update-inbox-name-text"
                  v-model="inboxNameModel"
                  :label="$t('component.inbox.new.name')"
                  outlined
                  dense
                  required
                />
                <v-text-field
                  id="update-inbox-deletion-deadline-text"
                  v-model.number="deletionDeadlineModel"
                  :label="$t('component.inbox.deletion.deadline')"
                  outlined
                  dense
                  required
                  :rules="[
                    v =>
                      (v >= deletionDeadline.min &&
                        v <= deletionDeadline.max) ||
                      errorNotification
                  ]"
                />
              </template>
              <template slot="tooltip">
                {{ $t('component.inbox.update') }}
              </template>
            </TableAction>
            <TableAction
              confirmation
              icon="mdi-delete"
              color="error"
              :title="$t('component.inbox.delete.finally')"
              @confirmed="deleteInbox(item)"
            >
              <template slot="confirmationContent">
                {{
                  $t('component.inbox.delete', { inboxname: item.inboxname })
                }}
              </template>
              <template slot="tooltip">
                {{ $t('component.inbox.delete.finally') }}
              </template>
            </TableAction>
          </div>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>
